/**
 * The PSL's institutional documents, migrated from the legacy
 * patinhassemlar.org site into Firebase Storage. URLs use the same
 * download-token pattern as pet photos (see
 * `patinhas-sem-lar-infra/scripts/migrate-docs/`), so they're plain
 * <a href> targets — no Firebase SDK, no auth, no Storage Rules
 * dance on this site.
 */

export type DocumentCategory =
  | 'constituicao'
  | 'atividades'
  | 'protocolos'
  | 'orgaos'
  | 'atas';

export interface InstitutionalDocument {
  id: string;
  title: string;
  /** Issue year. Some documents (Atas 1-3, 5) are undated in the source. */
  year: number | null;
  category: DocumentCategory;
  url: string;
  sizeBytes: number;
}

export interface DocumentCategoryMeta {
  id: DocumentCategory;
  /** Short label shown above the section. */
  label: string;
  /** Hand-written intro note, set in the Caveat handwriting face. */
  note: string;
  /** Tape colour for the cards in this group — rotates the visual rhythm. */
  tapeColor: 'sage' | 'terracotta' | 'ochre';
}

export const DOCUMENT_CATEGORIES: DocumentCategoryMeta[] = [
  {
    id: 'constituicao',
    label: '— Constituição & estatutos',
    note: '— como a associação nasceu e onde tem casa.',
    tapeColor: 'sage',
  },
  {
    id: 'atividades',
    label: '— Atividades & contas',
    note: '— o que fizemos e onde foi o dinheiro.',
    tapeColor: 'ochre',
  },
  {
    id: 'protocolos',
    label: '— Protocolos & parcerias',
    note: '— quem caminha connosco.',
    tapeColor: 'terracotta',
  },
  {
    id: 'orgaos',
    label: '— Órgãos sociais',
    note: '— quem assume a responsabilidade.',
    tapeColor: 'sage',
  },
  {
    id: 'atas',
    label: '— Atas das reuniões',
    note: '— o caderno por dentro.',
    tapeColor: 'ochre',
  },
];

export const DOCUMENTS: InstitutionalDocument[] = [
  // ── Constituição & estatutos ─────────────────────────────────────
  {
    id: 'estatutos-2013',
    title: 'Constituição e Estatutos da Associação',
    year: 2013,
    category: 'constituicao',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Festatutos-2013.pdf?alt=media&token=af876152-33b6-4dbe-8098-c4d8cc74e635',
    sizeBytes: 1691328,
  },
  {
    id: 'direito-superficie',
    title: 'Escritura — Direito de Superfície',
    year: 2024,
    category: 'constituicao',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fdireito-de-superficie-escritura.pdf?alt=media&token=8dd9c5c8-46fd-4fc3-a50c-7c9de1f4257d',
    sizeBytes: 3161775,
  },
  {
    id: 'parecer-camarario',
    title: 'Parecer Camarário',
    year: 2024,
    category: 'constituicao',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fparecer-camarario.pdf?alt=media&token=90a32865-4c3a-40ab-b3b3-eba7c5016195',
    sizeBytes: 1005670,
  },
  {
    id: 'camara-cede-terreno',
    title: 'Câmara de Espinho cede terreno à PSL',
    year: 2025,
    category: 'constituicao',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fcamara-espinho-cede-terreno.pdf?alt=media&token=8bd46dd1-563c-49fd-87b7-e06586cade08',
    sizeBytes: 220337,
  },
  {
    id: 'alteracao-estatutos-sede',
    title: 'Alteração de Estatutos — Sede da Associação',
    year: 2023,
    category: 'constituicao',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Falteracao-estatutos-sede.pdf?alt=media&token=1b86d6ed-26ed-4caf-b9e9-5f34b35eb9d6',
    sizeBytes: 1224563,
  },

  // ── Atividades & contas ─────────────────────────────────────────
  {
    id: 'relatorio-contas-2024',
    title: 'Relatório de Atividades e Contas',
    year: 2024,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Frelatorio-contas-2024.pdf?alt=media&token=fedf4ec5-650a-4609-a564-459488e6802b',
    sizeBytes: 175175,
  },
  {
    id: 'plano-atividades-2025',
    title: 'Plano de Atividades e Orçamento',
    year: 2025,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fplano-atividades-2025.pdf?alt=media&token=8c43eed6-801c-4abd-8952-a3c1d5690837',
    sizeBytes: 156092,
  },
  {
    id: 'relatorio-contas-2023',
    title: 'Relatório de Atividades e Contas',
    year: 2023,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Frelatorio-contas-2023.pdf?alt=media&token=a0698444-c4ef-425e-bd97-9521bcd28b44',
    sizeBytes: 1076527,
  },
  {
    id: 'plano-atividades-2024',
    title: 'Plano de Atividades e Orçamento',
    year: 2024,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fplano-atividades-2024.pdf?alt=media&token=cb1e127a-4933-446f-be50-df6f57bc3f6c',
    sizeBytes: 1026696,
  },
  {
    id: 'relatorio-contas-2025',
    title: 'Relatório de Atividades e Contas',
    year: 2025,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Frelatorio-contas-2025.pdf?alt=media&token=a333c63b-8392-47bd-9ad1-cef1c90850c4',
    sizeBytes: 1903962,
  },
  {
    id: 'plano-atividades-2023',
    title: 'Plano de Atividades e Orçamento',
    year: 2023,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fplano-atividades-2023.pdf?alt=media&token=c061aad9-bd13-4562-9caa-c47c2adc17e4',
    sizeBytes: 828527,
  },
  {
    id: 'relatorio-contas-2022',
    title: 'Relatório de Atividades e Contas',
    year: 2022,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Frelatorio-contas-2022.pdf?alt=media&token=4acbfccf-5e83-4a11-8d43-b859ba425c18',
    sizeBytes: 827617,
  },
  {
    id: 'plano-atividades-2022',
    title: 'Plano de Atividades e Orçamento',
    year: 2022,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fplano-atividades-2022.pdf?alt=media&token=680de0d5-ef43-41f0-a269-4c36700600b5',
    sizeBytes: 1012671,
  },
  {
    id: 'relatorio-contas-2021',
    title: 'Relatório de Atividades e Contas',
    year: 2021,
    category: 'atividades',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Frelatorio-contas-2021.pdf?alt=media&token=2e26e40c-4790-4642-84eb-2caee94a73db',
    sizeBytes: 1996334,
  },

  // ── Protocolos & parcerias ──────────────────────────────────────
  {
    id: 'protocolo-cm-2024-2025',
    title: 'Protocolo de Colaboração com a Câmara Municipal',
    year: 2024,
    category: 'protocolos',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fprotocolo-cm-2024-2025.pdf?alt=media&token=835f32a4-4426-4f92-a466-d696f525d1c4',
    sizeBytes: 2381359,
  },
  {
    id: 'noticia-orcamento-participativo',
    title: 'Notícia — Vencimento do Orçamento Participativo (Maré Viva)',
    year: 2024,
    category: 'protocolos',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fnoticia-orcamento-participativo.pdf?alt=media&token=dc51107c-373d-42dc-a9a7-6ce446172543',
    sizeBytes: 630876,
  },

  // ── Órgãos sociais ──────────────────────────────────────────────
  {
    id: 'orgaos-psl-2025',
    title: 'Tomada de Posse dos Órgãos Sociais',
    year: 2025,
    category: 'orgaos',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Forgaos-psl-2025.pdf?alt=media&token=cbb02035-b4cf-414b-b5a8-32881493a291',
    sizeBytes: 474290,
  },

  // ── Atas ────────────────────────────────────────────────────────
  {
    id: 'ata-1-2026',
    title: 'Ata nº 1/2026',
    year: 2026,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-1-2026.pdf?alt=media&token=7e7544a3-7920-4a9b-9c5d-037ca94964be',
    sizeBytes: 574093,
  },
  {
    id: 'ata-5',
    title: 'Ata nº 5',
    year: null,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-5.pdf?alt=media&token=24d52892-2699-436b-92ea-5907fca62dc3',
    sizeBytes: 50460,
  },
  {
    id: 'ata-4',
    title: 'Ata nº 4',
    year: 2024,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-4-2024.pdf?alt=media&token=7cfaf1cb-b1d8-4f57-95a9-ff2f13b80389',
    sizeBytes: 711903,
  },
  {
    id: 'ata-3',
    title: 'Ata nº 3 — com documentos das contas',
    year: null,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-3.pdf?alt=media&token=cd4771ed-34b6-499b-8a5b-84d90e8b023e',
    sizeBytes: 2180066,
  },
  {
    id: 'ata-2',
    title: 'Ata nº 2 — documentos das contas',
    year: null,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-2.pdf?alt=media&token=9ae7860f-ff70-4dc4-9da9-980d90669207',
    sizeBytes: 3247118,
  },
  {
    id: 'ata-1',
    title: 'Ata nº 1 — documentos das contas',
    year: null,
    category: 'atas',
    url: 'https://firebasestorage.googleapis.com/v0/b/key-mystery-450417-k4.firebasestorage.app/o/transparencia%2Fata-1.pdf?alt=media&token=881a4f97-2194-45a0-8d4d-7ade13f6e790',
    sizeBytes: 2321586,
  },
];

export function documentsByCategory(
  category: DocumentCategory,
): InstitutionalDocument[] {
  return DOCUMENTS.filter((doc) => doc.category === category);
}

/** Formats a byte count as a human label, e.g. "1.4 MB", "220 kB". */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(bytes >= 10_000_000 ? 0 : 1)} MB`;
  }
  if (bytes >= 1_000) {
    return `${Math.round(bytes / 1_000)} kB`;
  }
  return `${bytes} B`;
}
